import type { DurationInputArg } from '@/stores/timerange'
import { z } from 'zod'

const groupedFilterSearchParamSchema = z.string().optional().catch(undefined)

// `DurationInputArg` is a large dayjs literal union; a URL is already an untrusted boundary, so this
// widens to `string` and asserts the type rather than re-listing every literal here.
const durationUnitsSchema = z.string().transform(value => value as DurationInputArg)

const serializedRelativeTimeRangeSchema = z.object({
	type: z.literal('RelativeTimeRange'),
	duration: z.number(),
	units: durationUnitsSchema,
})

const serializedTimestampSchema = z.union([
	z.object({ type: z.literal('RelativeTimestamp'), duration: z.number(), units: durationUnitsSchema }),
	z.object({ type: z.literal('AbsoluteTimestamp'), date: z.number() }),
])

const serializedAbsoluteTimeRangeSchema = z.object({
	type: z.literal('AbsoluteTimeRange'),
	from: serializedTimestampSchema,
	to: serializedTimestampSchema,
})

const serializedNamedTimeRangeSchema = z.object({
	type: z.literal('NamedTimeRange'),
	name: z.string(),
})

/** Matches `SerializedTimeRange` (`@/stores/timerange`), so a URL's `range` round-trips through `deserializeTimeRange`. */
export const serializedTimeRangeSchema = z.union([
	serializedRelativeTimeRangeSchema,
	serializedAbsoluteTimeRangeSchema,
	serializedNamedTimeRangeSchema,
])

export const baseSearchSchema = z.object({
	page: z.number().int().positive().default(1),
	limit: z.number().int().positive().max(100).default(20),
	sort: z.string().optional(),
	order: z.enum(['asc', 'desc']).default('desc'),
	search: z.string().optional(),
	filters: z.string().optional(),
	tags: z.array(z.string()).optional(),
	status: z.string().optional(),
})

export const deviceSearchSchema = baseSearchSchema.extend({
	metadata: z.record(z.string(), z.union([z.string(), z.array(z.string())])).optional(),
	hardwareVersion: z.string().optional(),
	firmwareVersion: z.string().optional(),
	lastSeen: z.string().optional(),
	groups: groupedFilterSearchParamSchema,
})

export const streamsSearchSchema = baseSearchSchema.extend({
	streamType: z.string().optional(),
	groups: groupedFilterSearchParamSchema,
})

export const dashboardSearchSchema = baseSearchSchema.extend({
	category: z.string().optional(),
	shared: z.boolean().optional(),
})

export const sessionRulesSearchSchema = baseSearchSchema.extend({
	severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
	enabled: z.boolean().optional(),
})

export const dbcParsersSearchSchema = baseSearchSchema.extend({
	version: z.string().optional(),
	protocol: z.string().optional(),
})

/** Anomalies has no precedent to copy for time range and page: Audit Logs keeps both in local state, not
 *  the URL. This schema is what makes those two shareable. */
// Each field falls back rather than throwing: a hand-edited, stale, or future-incompatible URL must
// still open the page (into its default state), never the route's own error boundary.
export const anomaliesSearchSchema = z.object({
	page: z.number().int().positive().default(1).catch(1),
	order: z.enum(['asc', 'desc']).default('desc').catch('desc'),
	range: serializedTimeRangeSchema.optional().catch(undefined),
	groups: groupedFilterSearchParamSchema,
})

export const subscriptionPlansSearchSchema = z.object({
	openPlansModal: z.preprocess(val => val === 'true' || val === true, z.boolean()).optional(),
})

export type BaseSearch = z.infer<typeof baseSearchSchema>
export type DeviceSearch = z.infer<typeof deviceSearchSchema>
export type DashboardSearch = z.infer<typeof dashboardSearchSchema>
export type SessionRulesSearch = z.infer<typeof sessionRulesSearchSchema>
export type DbcParsersSearch = z.infer<typeof dbcParsersSearchSchema>
export type AnomaliesSearch = z.infer<typeof anomaliesSearchSchema>
export type SubscriptionPlansSearch = z.infer<typeof subscriptionPlansSearchSchema>

export const ACCESS_DENIED_ORG_OWNER_ONLY_REASON = 'org-owner-only' as const
export const ACCESS_DENIED_NO_PROJECT_ACCESS_REASON = 'no-project-access' as const
export const ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON = 'project-settings-admin-only' as const

export const accessDeniedSearchSchema = z.object({
	reason: z
		.union([
			z.literal(ACCESS_DENIED_ORG_OWNER_ONLY_REASON),
			z.literal(ACCESS_DENIED_NO_PROJECT_ACCESS_REASON),
			z.literal(ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON),
		])
		.optional(),
})

export type AccessDeniedSearch = z.infer<typeof accessDeniedSearchSchema>
